'use client'

import { type FC, useRef } from 'react'

import { Corridor } from '@/components/atoms/Corridor'
import { Grid } from '@/components/atoms/Grid'
import MapCluster from '@/components/atoms/MapCluster'
import MapMarker from '@/components/atoms/MapMarker'
import { useMapSelectedVenue } from '@/contexts/MapSelectedVenue'
import { createPortal } from 'react-dom'
import { isMarkerData } from './helpers'
import useMap from './hooks/useMap'
import useMarkers from './hooks/useMarkers'
import type { IMarkerData } from './types'

import 'mapbox-gl/dist/mapbox-gl.css'

export type VenuesMapProps = {
	markerData: IMarkerData[]
}

export const VenuesMap: FC<VenuesMapProps> = ({ markerData }) => {
	const mapContainerRef = useRef<HTMLDivElement | null>(null)
	const markersContainerRef = useRef<HTMLDivElement | null>(null)

	const { selectedVenue, setSelectedVenue } = useMapSelectedVenue()

	const map = useMap(
		mapContainerRef,
		markerData.length === 1
			? {
					marker: markerData[0],
					markersContainer: mapContainerRef,
				}
			: {},
	)

	const markers = useMarkers(map, markerData, mapContainerRef)

	return (
		<div className="relative h-full w-full">
			<div ref={mapContainerRef} className="h-full w-full rounded-sm">
				{markers.map(({ marker, data }) => {
					if (isMarkerData(data)) {
						return createPortal(
							<MapMarker
								data={data}
								onSelectVenue={setSelectedVenue}
								selected={selectedVenue === data.id}
							/>,
							marker.getElement(),
							data.id,
						)
					}
					return createPortal(
						<MapCluster data={data} />,
						marker.getElement(),
						`cluster:${data.cluster_id}`,
					)
				})}
			</div>
			<div className="absolute inset-0 pointer-events-none">
				<Corridor className="h-full">
					<Grid className="h-full">
						<div className="col-span-full">
							{/* Markers will fit into the bounds of this element */}
							<div ref={markersContainerRef} className="h-full" />
						</div>
					</Grid>
				</Corridor>
			</div>
		</div>
	)
}
